import React, { lazy, Suspense } from "react";
import {Route, Routes, Navigate, useSearchParams} from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import "./App.css";
import CompanyList from "./container/Search/CompanyList";
import {ForgotPassword} from "./container/Password/ForgotPassword";
import Signup from "./container/SignupPage";
import OnboardingPage from "./container/Onboarding";
import PrivateRoutes from "./routes/PrivateRoute";
import UpdatePasswordPage from "./container/User/UpdatePassword";
import PublicRoute from "./routes/PublicRoute";
import {CompanyDetailPage} from "./container/Company/CompanyDetailPage";
import AdminRoute from "./routes/AdminRoute";
import Workspaces from "./container/Workspaces/Workspaces";
import WorkspaceDetailGate from "./container/Workspaces/WorkspaceDetailGate";
import Chat from "./container/Chat/Chat";
import VerifyEmailLanding from "./container/EmailVerification/VerifyEmailLanding";
import CheckYourEmail from "./container/EmailVerification/CheckYourEmail";
import ResendVerification from "./container/EmailVerification/ResendVerification";
import ThanksPage from "./container/PublicCheckout/ThanksPage";
import {useRuntimeConfig} from "./config/RuntimeConfigProvider";

// Home V3-2 (FE #700) is its own chunk, so the dashboard does not weigh on the main bundle.
const Landing = lazy(() => import("./container/Landing"));
const Login = lazy(() => import("./container/Login"));
const ResetPassword = lazy(() => import("./container/Password/ResetPassword"));
const NotFound = lazy(() => import("./components/layouts/NotFound/Error"));
const ConferenceList = lazy(() => import("./container/Conference/ConferenceList"));
const ConferenceDetailPage = lazy(() => import("./container/Conference/ConferenceDetailPage")
  .then((module) => ({ default: module.ConferenceDetailPage })));
const Admin = lazy(() => import("./container/Admin"));
const Account = lazy(() => import("./container/Settings/Account"));
const Subscription = lazy(() => import("./container/Settings/Subscription"));
const AdjustPlan = lazy(() => import("./container/Settings/AdjustPlan"));
const Usage = lazy(() => import("./container/Settings/Usage"));
const ApiKeys = lazy(() => import("./container/Settings/ApiKeys"));
const Integrations = lazy(() => import("./container/Settings/Integrations"));
const HelpCenter = lazy(() => import("./container/Help/HelpCenter"));
const HubSpotGuide = lazy(() => import("./container/Help/HubSpotGuide"));
const SupportPage = lazy(() => import("./container/Help/SupportPage"));

/** Stripe redirects to /credits?success=true or /credits?cancelled=true.
 *  Forward to the canonical /settings/usage path, preserving query params. */
function CreditsRedirect() {
  const [searchParams] = useSearchParams();
  return <Navigate to={`/settings/usage?${searchParams.toString()}`} replace />;
}

function App() {
  const {PUBLIC_SIGNUP_ENABLED} = useRuntimeConfig();
  // Temporary public-signup gate: remove when FE #330 lands.
  const publicSignupEnabled = PUBLIC_SIGNUP_ENABLED === "true";

  return (
    <ErrorBoundary>
      <Suspense fallback={<div role="status" aria-live="polite">Loading page…</div>}>
      <Routes>
        <Route path="/verify-email/check" element={<CheckYourEmail />} />
        <Route path="/verify-email/resend" element={<ResendVerification />} />
        <Route path="/verify-email/:token" element={<VerifyEmailLanding />} />
        <Route path="/password/reset/:authCode" element={<ResetPassword />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        {/* Stripe public-checkout success page: outside both guards so it renders signed in or out. */}
        <Route path="/thanks" element={<ThanksPage />} />
        <Route path="/help/hubspot" element={<HubSpotGuide />} />
        <Route path="/support" element={<SupportPage />} />

        <Route exact element={<PublicRoute />}>
          <Route path="/" element={<Login />} />
          {/* Legacy invite links arrive at /page?token=... and redirect to signup. */}
          <Route path="/page" element={<NotFound />} />
          <Route path="/password/forgot" element={<ForgotPassword />} />
          {publicSignupEnabled && <Route path="/signup" element={<Signup />} />}
          <Route path="/signup/:inviteCode" element={<Signup />} />
        </Route>

        <Route exact element={<PrivateRoutes />}>
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/landing" element={<Landing />} />
          <Route path="/change-password" element={<UpdatePasswordPage />} />
          <Route path="/lists/companies/:searchKey?" element={<CompanyList />} />
          <Route path="/company/:stagedId?" element={<CompanyDetailPage />} />
          <Route path="/conferences" element={<ConferenceList />} />
          <Route path="/conference/:conferenceId" element={<ConferenceDetailPage />} />
          <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
          <Route path="/settings/account" element={<Account />} />
          <Route path="/settings/subscription" element={<Subscription />} />
          <Route path="/settings/adjust-plan" element={<AdjustPlan />} />
          <Route path="/settings/usage" element={<Usage />} />
          <Route path="/settings/apikeys" element={<ApiKeys />} />
          <Route path="/settings/integrations" element={<Integrations />} />
          <Route path="/credits" element={<CreditsRedirect />} />
          <Route path="/workspaces" element={<Workspaces />} />
          <Route path="/workspaces/:id" element={<WorkspaceDetailGate />} />
          <Route path="/chat" element={<Chat />} />
          <Route path="/help" element={<HelpCenter />} />
          {/* The standalone chat index is retired; the modern shell owns history. */}
          <Route path="/chats" element={<Navigate to="/chat" replace />} />
          {/* Catch-all lives behind the private guard: a signed-out visitor at any
              non-public path (unknown ones included) is sent to Login, while a
              signed-in user still sees the not-found view. */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
      </Suspense>
    </ErrorBoundary>
  )
}

export default App;
